import type { StylisElement, StylisPlugin } from "@emotion/cache";

/**
 * Плагин stylis для MUI/emotion справа налево — то, что делает
 * stylis-plugin-rtl (cssjanus), но без новой зависимости: зеркалит каждое
 * объявление стиля. Нужен только арабскому интерфейсу (rtlEmotionCache); всем
 * остальным стили не трогаются вовсе.
 *
 *  - свойства: margin-left ↔ margin-right, left ↔ right,
 *    border-top-left-radius ↔ border-top-right-radius …;
 *  - слова left/right: float, clear, text-align, transform-origin,
 *    background-position, transition (имена свойств), cursor e-/w-resize;
 *  - четыре стороны: margin/padding/border-width/… «t r b l» → «t l b r»;
 *    border-radius «a b c d» → «b a d c»;
 *  - тени: знак сдвига по горизонтали;
 *  - «/* @noflip *\/» перед объявлением — оставить как есть.
 *
 * transform: translateX() не зеркалится (как и в cssjanus).
 */

const DECLARATION = "decl";
const COMMENT = "comm";

const swapWord = (w: string): string => (w === "left" ? "right" : w === "right" ? "left" : w);

/** margin-left → margin-right; border-top-left-radius → border-top-right-radius. */
export const flipProperty = (prop: string): string =>
    prop.startsWith("--") ? prop : prop.split("-").map(swapWord).join("-");

/** Значение — на части верхнего уровня: пробелы и запятые внутри скобок и кавычек не делят. */
const splitTop = (value: string, sep: "space" | "comma"): string[] => {
    const out: string[] = [];
    let cur = "";
    let depth = 0;
    let quote = "";
    for (const ch of value) {
        if (quote) {
            cur += ch;
            if (ch === quote) quote = "";
            continue;
        }
        if (ch === '"' || ch === "'") {
            quote = ch;
            cur += ch;
            continue;
        }
        if (ch === "(") depth += 1;
        else if (ch === ")") depth = Math.max(0, depth - 1);
        const isSep = depth === 0 && (sep === "space" ? /\s/.test(ch) : ch === ",");
        if (isSep) {
            if (sep === "comma" || cur) out.push(cur);
            cur = "";
            continue;
        }
        cur += ch;
    }
    if (sep === "comma" || cur) out.push(cur);
    return out;
};

const swapWordsIn = (value: string): string => value.replace(/\b(left|right)\b/g, (w) => swapWord(w));

const FOUR_SIDES = new Set([
    "margin",
    "padding",
    "border-width",
    "border-style",
    "border-color",
    "inset",
    "scroll-margin",
    "scroll-padding",
]);

const KEYWORD_PROPS = new Set([
    "float",
    "clear",
    "text-align",
    "text-align-last",
    "transform-origin",
    "perspective-origin",
    "object-position",
    "transition",
    "transition-property",
    "will-change",
]);

const SHADOW_PROPS = new Set(["box-shadow", "text-shadow", "-webkit-box-shadow", "-moz-box-shadow"]);

const CURSORS: Record<string, string> = {
    "e-resize": "w-resize",
    "w-resize": "e-resize",
    "ne-resize": "nw-resize",
    "nw-resize": "ne-resize",
    "se-resize": "sw-resize",
    "sw-resize": "se-resize",
    "nesw-resize": "nwse-resize",
    "nwse-resize": "nesw-resize",
};

const LENGTH = /^(-?)((?:\d*\.)?\d+)([a-z%]*)$/i;

const flipShadow = (value: string): string =>
    splitTop(value, "comma")
        .map((one) => {
            const parts = splitTop(one.trim(), "space");
            const i = parts.findIndex((p) => LENGTH.test(p));
            if (i < 0) return one;
            const m = parts[i].match(LENGTH) as RegExpMatchArray;
            if (Number(m[2]) !== 0) parts[i] = (m[1] ? "" : "-") + m[2] + m[3];
            return parts.join(" ");
        })
        .join(",");

const flipFourSides = (value: string): string => {
    const p = splitTop(value, "space");
    return p.length === 4 ? [p[0], p[3], p[2], p[1]].join(" ") : value;
};

const flipRadius = (value: string): string =>
    value
        .split("/")
        .map((side) => {
            const p = splitTop(side.trim(), "space");
            if (p.length === 2) return [p[1], p[0]].join(" ");
            if (p.length === 3) return [p[1], p[0], p[1], p[2]].join(" ");
            if (p.length === 4) return [p[1], p[0], p[3], p[2]].join(" ");
            return side.trim();
        })
        .join(" / ");

const flipBackgroundPosition = (value: string): string =>
    splitTop(value, "comma")
        .map((one) => {
            const parts = splitTop(one.trim(), "space");
            if (parts.length && /^(\d*\.)?\d+%$/.test(parts[0]) && !parts.some((p) => /^(left|right)$/.test(p))) {
                parts[0] = `${100 - parseFloat(parts[0])}%`;
                return parts.join(" ");
            }
            return swapWordsIn(one);
        })
        .join(",");

/** Объявление «свойство: значение» справа налево. */
export const flipDeclaration = (prop: string, value: string): { prop: string; value: string } => {
    const name = prop.trim();
    if (name.startsWith("--")) return { prop, value };
    const important = /\s*!important\s*$/i.exec(value);
    let v = important ? value.slice(0, important.index) : value;
    const lower = name.toLowerCase();

    if (FOUR_SIDES.has(lower)) v = flipFourSides(v);
    else if (lower === "border-radius") v = flipRadius(v);
    else if (SHADOW_PROPS.has(lower)) v = flipShadow(v);
    else if (lower === "background-position" || lower === "background-position-x") v = flipBackgroundPosition(v);
    else if (lower === "cursor") v = CURSORS[v.trim()] || v;
    else if (KEYWORD_PROPS.has(lower)) v = swapWordsIn(v);

    return { prop: flipProperty(name), value: important ? v + important[0].trim() : v };
};

const noflipBefore = (siblings: StylisElement[] | undefined, index: number): boolean => {
    const prev = siblings && index > 0 ? siblings[index - 1] : undefined;
    return !!prev && prev.type === COMMENT && typeof prev.value === "string" && prev.value.includes("@noflip");
};

/** Плагин для createCache({ stylisPlugins: [stylisRtl, prefixer] }) — до префиксов. */
export const stylisRtl: StylisPlugin = (element, index, children) => {
    if (element.type !== DECLARATION) return;
    if (typeof element.props !== "string" || typeof element.children !== "string") return;
    if (noflipBefore(children, index)) return;
    const out = flipDeclaration(element.props, element.children);
    if (out.prop === element.props && out.value === element.children) return;
    element.props = out.prop;
    element.children = out.value;
    element.value = `${out.prop}:${out.value};`;
    element.length = out.prop.length;
};
